import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import clsx from "clsx";
import AddIcon from "@material-ui/icons/Add";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import ProjectIcon from "@material-ui/icons/LayersOutlined";
import DatasetIcon from "@material-ui/icons/TableChartOutlined";
import { format } from "date-fns";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useProject } from "../../context/Project/ProjectContext";
import { useConfirm } from "../../components/ConfirmDialog";
import { useMenuStyles } from "../../components/menuStyles";
import DatasetFieldsEditor, { FIELD_TYPES } from "./DatasetFieldsEditor";

const T = {
  bg: "#F6F7F9",
  panel: "#FFFFFF",
  line: "#E6E9ED",
  hover: "#FAFBFC",
  ink: "#1B222A",
  ink2: "#5B6470",
  ink3: "#929AA4",
};

const useStyles = makeStyles(theme => ({
  page: { background: T.bg, minHeight: "100%" },
  wrap: { maxWidth: 1080, margin: "0 auto", padding: theme.spacing(4, 3) },
  header: { display: "flex", alignItems: "flex-start", marginBottom: theme.spacing(3) },
  headIcon: { width: 40, height: 40, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#EEF0F3", color: "#5B6470", marginRight: 14, marginTop: 2 },
  title: { fontFamily: theme.app.heading, fontSize: 22, fontWeight: 700, color: theme.app.ink },
  subtitle: { fontSize: 13.5, color: T.ink3, marginTop: 4, maxWidth: 640 },
  grow: { flex: 1 },
  list: { display: "flex", flexDirection: "column", gap: 10 },
  card: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    background: T.panel,
    border: `1px solid ${T.line}`,
    borderRadius: 12,
    padding: theme.spacing(1.5, 2),
    cursor: "pointer",
    "&:hover": { background: T.hover },
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#EAF6EF",
    color: "#1E9E62",
    flexShrink: 0,
  },
  main: { minWidth: 0, flex: 1 },
  infoLabel: { fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: T.ink3 },
  name: { fontSize: 15, fontWeight: 600, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  col: { width: 150, flexShrink: 0 },
  colVal: { fontSize: 13.5, color: T.ink2 },
  empty: { textAlign: "center", color: T.ink3, padding: theme.spacing(8, 2), fontSize: 14 },
  fieldRow: { marginTop: theme.spacing(2) },
}));

const Datasets = () => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const history = useHistory();
  const { activeProjectId, projects } = useProject();
  const { confirm } = useConfirm();

  const [datasets, setDatasets] = useState([]);
  const [rowMenu, setRowMenu] = useState(null); // { anchor, ds }
  const [projMenu, setProjMenu] = useState(null); // { anchor, ds } — mover p/ projeto
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newFields, setNewFields] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!activeProjectId) {
      setDatasets([]);
      return;
    }
    try {
      const { data } = await api.get("/datasets", { params: { projectId: activeProjectId } });
      setDatasets(data.datasets || []);
    } catch (err) {
      toastError(err);
    }
  }, [activeProjectId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setNewName("");
    setNewFields([{ name: "", label: "", type: "texto", options: "" }]);
    setCreateOpen(true);
  };

  const create = async () => {
    setSaving(true);
    try {
      const fields = newFields
        .filter(f => String(f.name || "").trim())
        .map(f => {
          const out = { name: f.name.trim(), label: f.label || undefined, type: f.type };
          if (f.type === "opcao" && f.options) out.options = String(f.options).split(",").map(s => s.trim()).filter(Boolean);
          if (f.indexed) out.indexed = true;
          return out;
        });
      const { data } = await api.post("/datasets", { projectId: activeProjectId, name: newName, fields });
      setCreateOpen(false);
      toast.success("Tabela criada.");
      history.push(`/datasets/${data.dataset.id}`);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const remove = async ds => {
    setRowMenu(null);
    if (!(await confirm({ title: "Excluir tabela", message: `Excluir "${ds.name}" e todos os seus registros? Não pode ser desfeito.`, confirmText: "Excluir", danger: true }))) return;
    try {
      await api.delete(`/datasets/${ds.id}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  // move a tabela para outro projeto (relatórios acompanham). Some desta lista.
  const moveToProject = async (ds, projectId) => {
    setProjMenu(null);
    try {
      await api.put(`/datasets/${ds.id}/project`, { projectId });
      const proj = projects.find(p => p.id === projectId);
      toast.success(`Tabela movida para "${(proj && proj.name) || "o projeto"}".`);
      setDatasets(list => list.filter(x => x.id !== ds.id));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.header}>
          <div className={classes.headIcon}><DatasetIcon /></div>
          <div>
            <Typography className={classes.title}>Tabelas</Typography>
            <Typography className={classes.subtitle}>
              Tabelas que suas automações e agentes alimentam — cada gravação vira um registro. Base para os relatórios.
            </Typography>
          </div>
          <div className={classes.grow} />
          <Button variant="contained" color="primary" disableElevation startIcon={<AddIcon />} onClick={openCreate} disabled={!activeProjectId}>
            Nova tabela
          </Button>
        </div>

        {datasets.length === 0 ? (
          <div className={classes.empty}>
            Nenhuma tabela ainda.<br />
            Crie o primeiro e use o nó <b>Salvar registro</b> numa automação para alimentá-lo.
          </div>
        ) : (
          <div className={classes.list}>
            {datasets.map(ds => (
              <div key={ds.id} className={classes.card} onClick={() => history.push(`/datasets/${ds.id}`)}>
                <div className={classes.avatar}><DatasetIcon /></div>
                <div className={classes.main}>
                  <div className={classes.infoLabel}>Nome</div>
                  <div className={classes.name}>{ds.name}</div>
                </div>
                <div className={classes.col}>
                  <div className={classes.infoLabel}>Registros</div>
                  <span className={classes.colVal}>{ds.recordCount ?? 0}</span>
                </div>
                <div className={classes.col}>
                  <div className={classes.infoLabel}>Último registro</div>
                  <span className={classes.colVal}>{ds.lastRecordAt ? format(new Date(ds.lastRecordAt), "dd/MM/yyyy HH:mm") : "—"}</span>
                </div>
                <span onClick={e => e.stopPropagation()} style={{ display: "flex" }}>
                  <IconButton size="small" onClick={e => setRowMenu({ anchor: e.currentTarget, ds })}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Menu anchorEl={rowMenu ? rowMenu.anchor : null} open={!!rowMenu} onClose={() => setRowMenu(null)} PaperProps={{ className: menu.paper }} MenuListProps={{ disablePadding: true }}>
        <MenuItem className={menu.item} onClick={e => rowMenu && setProjMenu({ anchor: e.currentTarget, ds: rowMenu.ds })}>
          <ProjectIcon fontSize="small" />
          Mover para projeto…
        </MenuItem>
        <MenuItem className={clsx(menu.item, menu.danger)} onClick={() => rowMenu && remove(rowMenu.ds)}>
          <DeleteIcon fontSize="small" />
          Excluir tabela
        </MenuItem>
      </Menu>

      {/* project picker — submenu de "Mover para projeto…" */}
      <Menu
        anchorEl={projMenu ? projMenu.anchor : null}
        open={!!projMenu}
        onClose={() => { setProjMenu(null); setRowMenu(null); }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: menu.paper }}
        MenuListProps={{ disablePadding: true }}
      >
        {projects.length <= 1 && <MenuItem className={menu.item} disabled>Nenhum outro projeto</MenuItem>}
        {projects.map(p => {
          const isCurrent = p.id === activeProjectId;
          return (
            <MenuItem
              key={p.id}
              className={menu.item}
              disabled={isCurrent}
              onClick={() => { const ds = projMenu && projMenu.ds; setRowMenu(null); if (ds) moveToProject(ds, p.id); }}
            >
              {p.name}{isCurrent ? " (atual)" : ""}
            </MenuItem>
          );
        })}
      </Menu>

      <Dialog open={createOpen} onClose={saving ? undefined : () => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova tabela</DialogTitle>
        <DialogContent dividers>
          <TextField label="Nome da tabela (ex.: Mensagens enviadas)" value={newName} onChange={e => setNewName(e.target.value)} fullWidth variant="outlined" size="small" autoFocus />
          <div className={classes.fieldRow}>
            <DatasetFieldsEditor fields={newFields} onChange={setNewFields} types={FIELD_TYPES} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={create} color="primary" variant="contained" disableElevation disabled={saving || !String(newName).trim()}>
            {saving ? "Criando…" : "Criar tabela"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Datasets;
